// useJarvis — primary hook for all JARVIS AI operations.
// Components call this hook instead of calling claudeAI.js directly,
// so all loading/error state stays in context automatically.

import { useCallback } from 'react';
import { useJarvisContext, A } from '../context/JarvisContext';
import {
  askJarvis,
  analyzeScreenshot,
  analyzePineScript,
  extractAndSaveScript,
  extractTranscript as _extractTranscript,
  processDailyRecap,
  generateWeeklyBriefing,
} from '../services/claudeAI';
import { generateBridgePrompt, processBridgeResponse } from '../services/claudeBridge';
import { isTradeAnalysisIntent, analyzeAllTimeframes, formatMultiTimeframeReply } from '../services/scriptAnalysis';
import { autoGenerateStrategies, autoProposedHypotheses, evolveStrategies } from '../engines/jarvisBrain';
import { autoLearnFromChat } from '../services/jarvisAutoLearn';
import { saveKnowledgeChunk, saveRule } from '../services/storage';
import { speak } from '../services/voiceService';

export const useJarvis = () => {
  const { state, dispatch, refreshData } = useJarvisContext();

  // ── Send a chat message ──────────────────────────────────────────────
  // Screenshots: base64 is sent to JARVIS for analysis ONLY.
  // It is NEVER stored in localStorage or message history.
  // After JARVIS reads it, the data is gone from memory automatically.
  const sendMessage = useCallback(async (text, screenshot = null) => {
    if (!text?.trim() && !screenshot) return;

    const hasScreenshot = !!screenshot;
    const t = (text || '').toLowerCase();

    // ── Save command intercept — "save this", "save this rule", "save this logic" ──
    const isSaveCmd = t.includes('save this') || t.includes('save rule') ||
                      t.includes('save logic') || t.includes('jarvis save') ||
                      t.includes('save pattern') || t.includes('save level') ||
                      t.includes('he save kar') || t.includes('memory madhe save') ||
                      t.includes('he jarvis la dya') || t.includes('save kar');

    if (isSaveCmd && !screenshot) {
      // Find last JARVIS reply to save
      const lastReply = [...state.messages].reverse().find(m => m.role === 'assistant');
      const lastUserQ = [...state.messages].reverse().find(m => m.role === 'user' && m.content !== text);

      const contentToSave = lastReply?.content || '';
      const title = lastUserQ?.content?.slice(0, 80) || 'Chat Logic Save';

      dispatch({ type: A.ADD_MESSAGE, payload: { role: 'user', content: text } });
      dispatch({ type: A.SET_INPUT, payload: '' });

      if (contentToSave) {
        saveKnowledgeChunk({
          source:   'Chat — Voice/Text Save',
          category: 'rule',
          title:    title,
          content:  contentToSave.slice(0, 500),
          tags:     ['chat-save', 'user-saved', 'logic'],
        });

        // If reply has BUY/SELL signal, also save as rule
        const hasTrade = /\bBUY\b|\bSELL\b/i.test(contentToSave);
        if (hasTrade) {
          saveRule({
            seed_id:       `chat_save_${Date.now()}`,
            title:         title,
            condition:     'From chat discussion',
            action:        /\bBUY\b/i.test(contentToSave) ? 'BUY' : 'SELL',
            source:        'Chat — User Saved',
            auto_extracted: false,
            notes:         contentToSave.slice(0, 200),
          });
        }

        const savedMsg = `JARVIS Memory Updated. Saved to Knowledge Base: ${title}. He future analysis madhe use hoil, Sir.`;
        dispatch({ type: A.ADD_MESSAGE, payload: {
          role: 'assistant',
          content: `✅ **JARVIS Memory Updated!**\n\n📚 Saved to Knowledge Base:\n> "${title}"\n\nHe future analysis madhe use hoil, Sir. Rules + Knowledge Base dono madhe add kele.`,
        }});
        speak(savedMsg);
      } else {
        dispatch({ type: A.ADD_MESSAGE, payload: {
          role: 'assistant',
          content: '⚠️ Save karanyasathi aadhi kahi discuss kara Sir. Maghe "save this" sanga.',
        }});
        speak('Save karanyasathi aadhi kahi discuss kara Sir.');
      }
      return;
    }

    // User message — store text + a flag, never the raw base64
    dispatch({
      type: A.ADD_MESSAGE,
      payload: {
        role:       'user',
        content:    text || '(Chart screenshot for analysis)',
        screenshot: hasScreenshot,  // just a boolean flag — no image data stored
      },
    });
    dispatch({ type: A.THINKING_START });
    dispatch({ type: A.SET_INPUT, payload: '' });

    // ── Trade-decision questions bypass Groq entirely ────────────────────
    // "Bias/setup/trade/buy/sell/आज काय" etc. → answered from A_Signal.js's
    // real Structure+Fibonacci+ADX math across every validated timeframe,
    // never from an LLM's free-form guess. Sir explicitly flagged Groq's
    // chat analysis as wrong — this is the fix: for trade-decision intent,
    // there is no "asking the AI" step at all.
    if (isTradeAnalysisIntent(text) && !screenshot) {
      try {
        const results = await analyzeAllTimeframes();
        const reply = formatMultiTimeframeReply(results);
        dispatch({ type: A.ADD_MESSAGE, payload: { role: 'assistant', content: reply } });
        speak(reply);
        refreshData();
      } catch (err) {
        dispatch({ type: A.SET_ERROR, payload: err.message });
        dispatch({ type: A.ADD_MESSAGE, payload: { role: 'assistant', content: `⚠️ Script analysis error: ${err.message}`, isError: true } });
      } finally {
        dispatch({ type: A.THINKING_STOP });
      }
      return;
    }

    try {
      // Conversation history for context — text only, no images
      const history = state.messages
        .slice(-12)
        .map(m => ({ role: m.role, content: m.content }));

      // Pass screenshot to JARVIS — used once, then the reference is dropped
      const reply = await askJarvis(text, history, screenshot);
      // screenshot variable goes out of scope here — GC will clean it up

      dispatch({ type: A.ADD_MESSAGE, payload: { role: 'assistant', content: reply } });
      speak(reply);

      // Auto-learn from every chat reply — updates rules, psychology, predictions silently
      autoLearnFromChat(text || '', reply);

      refreshData();
    } catch (err) {
      dispatch({ type: A.SET_ERROR, payload: err.message });
      dispatch({
        type: A.ADD_MESSAGE,
        payload: {
          role:    'assistant',
          content: `⚠️ Error: ${err.message}. Check your Groq API key in .env`,
          isError: true,
        },
      });
    } finally {
      dispatch({ type: A.THINKING_STOP });
    }
  }, [state.messages, dispatch, refreshData]);

  // ── Analyze a chart screenshot ────────────────────────────────────────
  const analyzeChart = useCallback(async (base64Image, note = '') => {
    dispatch({ type: A.THINKING_START });
    try {
      const reply = await analyzeScreenshot(base64Image, note);
      dispatch({ type: A.ADD_MESSAGE, payload: { role: 'user', content: note || '📸 Chart sent for analysis', screenshot: true } });
      dispatch({ type: A.ADD_MESSAGE, payload: { role: 'assistant', content: reply } });
      speak(reply);
      refreshData();
      return reply;
    } catch (err) {
      dispatch({ type: A.SET_ERROR, payload: err.message });
    } finally {
      dispatch({ type: A.THINKING_STOP });
    }
  }, [dispatch, refreshData]);

  // ── Analyze Pine Script ───────────────────────────────────────────────
  // Deliberately not spoken (unlike the other reply paths) — a code
  // review reply is long-form technical text meant to be read, not heard.
  const analyzePine = useCallback(async (code) => {
    dispatch({ type: A.THINKING_START });
    try {
      const reply = await analyzePineScript(code);
      dispatch({ type: A.ADD_MESSAGE, payload: { role: 'user', content: '📜 Pine Script sent for analysis' } });
      dispatch({ type: A.ADD_MESSAGE, payload: { role: 'assistant', content: reply } });
      refreshData();
      return reply;
    } catch (err) {
      dispatch({ type: A.SET_ERROR, payload: err.message });
    } finally {
      dispatch({ type: A.THINKING_STOP });
    }
  }, [dispatch, refreshData]);

  // ── Process daily recap ───────────────────────────────────────────────
  const submitDailyRecap = useCallback(async (recap) => {
    dispatch({ type: A.THINKING_START });
    try {
      const analysis = await processDailyRecap(recap);
      refreshData();
      return analysis;
    } catch (err) {
      dispatch({ type: A.SET_ERROR, payload: err.message });
      throw err;
    } finally {
      dispatch({ type: A.THINKING_STOP });
    }
  }, [dispatch, refreshData]);

  // ── Weekly briefing ───────────────────────────────────────────────────
  const runWeeklyBriefing = useCallback(async (weekData) => {
    dispatch({ type: A.THINKING_START });
    try {
      const briefing = await generateWeeklyBriefing(weekData);
      refreshData();
      return briefing;
    } catch (err) {
      dispatch({ type: A.SET_ERROR, payload: err.message });
      throw err;
    } finally {
      dispatch({ type: A.THINKING_STOP });
    }
  }, [dispatch, refreshData]);

  // ── Extract rules from a Pine Script + save to Rule Book ─────────────
  const extractScript = useCallback(async (code, title) => {
    dispatch({ type: A.THINKING_START });
    try {
      const result = await extractAndSaveScript(code, title);
      refreshData();
      return result;
    } catch (err) {
      dispatch({ type: A.SET_ERROR, payload: err.message });
      throw err;
    } finally {
      dispatch({ type: A.THINKING_STOP });
    }
  }, [dispatch, refreshData]);

  // ── Extract structured knowledge from YouTube transcript / notes ──────
  const extractTranscript = useCallback(async (text, sourceName) => {
    dispatch({ type: A.THINKING_START });
    try {
      const result = await _extractTranscript(text, sourceName);
      refreshData();
      return result;
    } catch (err) {
      dispatch({ type: A.SET_ERROR, payload: err.message });
      throw err;
    } finally {
      dispatch({ type: A.THINKING_STOP });
    }
  }, [dispatch, refreshData]);

  // ── Claude Bridge ─────────────────────────────────────────────────────
  const getBridgePrompt = useCallback((type, payload) => {
    return generateBridgePrompt(type, payload);
  }, []);

  const applyBridgeResponse = useCallback((type, rawResponse) => {
    const result = processBridgeResponse(type, rawResponse);
    refreshData();
    return result;
  }, [refreshData]);

  // ── JARVIS Self-Brain ─────────────────────────────────────────────────
  const runBrainAnalysis = useCallback(() => {
    const newStrategies  = autoGenerateStrategies();
    const newHypotheses  = autoProposedHypotheses();
    const evolution      = evolveStrategies();
    refreshData();
    return { newStrategies, newHypotheses, evolution };
  }, [refreshData]);

  return {
    // State
    isThinking:    state.isThinking,
    lastError:     state.lastError,
    messages:      state.messages,
    inputText:     state.inputText,

    // Actions
    sendMessage,
    analyzeChart,
    analyzePine,
    extractScript,
    extractTranscript,
    submitDailyRecap,
    runWeeklyBriefing,
    getBridgePrompt,
    applyBridgeResponse,
    runBrainAnalysis,

    // Helpers
    clearError:    () => dispatch({ type: A.CLEAR_ERROR }),
    clearMessages: () => dispatch({ type: A.CLEAR_MESSAGES }),
    setInput:      (val) => dispatch({ type: A.SET_INPUT, payload: val }),
  };
};
